import * as Cookies from "es-cookie";

// Per-module View/Edit/Delete permissions for the logged-in user's role.
// At login we store getRoleModuleAccess() rows in localStorage("roleModuleAccess"):
//   [{ module_key, module_name, sub_module_name, is_view, is_edit, is_delete }, ...]
// Use these helpers to gate UI actions so every module honors its granted access.

function getRoleModuleAccess() {
  try {
    const raw = localStorage.getItem("roleModuleAccess");
    if (raw && raw !== "" && raw !== "undefined") return JSON.parse(raw) || [];
  } catch (e) { /* ignore */ }
  return [];
}

// The backend is inconsistent about booleans: is_edit comes back as 1, "1", or true
// depending on the resolver. Normalise once. (Same rule as the sidebar's isGranted.)
const isGranted = (v) => v === 1 || v === "1" || v === true || v === "true";

// Institute admins / management (employee_type 1 & 2) have full access.
function isFullAccess() {
  const ul = String(Cookies.get("userlevel") || Cookies.get("user_level") || Cookies.get("role_id") || "");
  return ul === "1" || ul === "2" || ul === "admin";
}

// Does this permission row describe one of the modules we're asking about?
// Rows are matched on module_key ("Student_Doubts") OR module_name ("Doubts"):
// some resolvers return only the human-readable name, and matching on module_key
// alone would find no row — which `can()` reads as "not restricted" and would
// silently grant edit/delete to every role. Callers pass both spellings.
function rowMatches(row, keys) {
  return keys.some(
    (k) => String(row.module_key || "") === k || String(row.module_name || "") === k
  );
}

// can(moduleKey, action, subModule?) -> boolean
//   moduleKey: a key, or an array of accepted spellings (module_key / module_name)
//   action: "view" | "edit" | "delete"
// Rules:
//   - Admins (userlevel 1/2): always allowed.
//   - No role permission rows at all (unconfigured): allowed (don't block).
//   - No row for this module: allowed (module not restricted).
//   - Matching row(s): allowed if any grants the action (is_<action> is truthy).
//   - Pass subModule to check a specific sub-module row.
export function can(moduleKey, action = "view", subModule = null) {
  if (isFullAccess()) return true;
  const rows = getRoleModuleAccess();
  if (!rows.length) return true;
  const keys = (Array.isArray(moduleKey) ? moduleKey : [moduleKey]).map((k) => String(k || ""));
  let matches = rows.filter((r) => rowMatches(r, keys));
  if (subModule != null) {
    matches = matches.filter((r) => String(r.sub_module_name || "") === String(subModule));
  }
  if (!matches.length) return true;
  const field = action === "edit" ? "is_edit" : action === "delete" ? "is_delete" : "is_view";
  return matches.some((r) => isGranted(r[field]));
}

export const canView = (moduleKey, subModule) => can(moduleKey, "view", subModule);
export const canEdit = (moduleKey, subModule) => can(moduleKey, "edit", subModule);
export const canDelete = (moduleKey, subModule) => can(moduleKey, "delete", subModule);
